
import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { MdLogout, MdChevronRight, MdSettings } from "react-icons/md";
import { AiOutlineClose } from "react-icons/ai";

import { leftSidebarLinks } from "../constants";
import { getMediaUrl } from "../lib/utils";
import ToastMessage from "./ToastMessage";
import { useUserContext } from "../context/AuthContext";
import { API } from "../api";

const LeftSidebar_mobile = ({ user, isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setIsAuthenticated, setAuthUser } = useUserContext();

  const sidebarRef = useRef(null);
  const [visible, setVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) setVisible(true);
  }, [isOpen]);

  const closeSidebar = () => {
    setVisible(false);
    setTimeout(() => setIsOpen(false), 300);
  };

  const logout = async (e) => {
    e.preventDefault();

    try {
      const res = await API.post("/logout");

      setIsAuthenticated(false);
      setAuthUser(null);

      navigate("/auth", { replace: true });
      ToastMessage(res.data.message);
      closeSidebar();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) closeSidebar();
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!isOpen && !visible) return null;

  const isAccountActive = pathname === "/settings/account" || pathname.startsWith("/settings/account/");

  return (
    <>
      <div onClick={closeSidebar} className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${visible ? "opacity-100" : "pointer-events-none opacity-0"}`} />

      <aside ref={sidebarRef} className={`fixed left-0 top-0 z-50 flex h-dvh w-[85%] max-w-[320px] flex-col overflow-hidden border-r border-gray-100 bg-white shadow-[12px_0_40px_rgba(0,0,0,0.10)] transition-transform duration-300 ease-out ${visible ? "translate-x-0" : "-translate-x-full"}`}>
        {/* ================= HEADER ================= */}

        <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-gray-100 px-5">
          <Link to="/" onClick={closeSidebar} className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50">
              <img src="/assets/images/logo.png" alt="Alakoze" className="h-8 w-8 object-contain" />
            </div>

            <span className="text-lg font-extrabold tracking-tight text-gray-900">
              Alakoze<span className="text-purple-600">.</span>
            </span>
          </Link>

          <button type="button" onClick={closeSidebar} className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-50 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-900 active:scale-95">
            <AiOutlineClose size={19} />
          </button>
        </div>

        {/* ================= CONTENT ================= */}

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 pt-5">
          {/* ================= PROFILE CARD ================= */}

          <Link to={`/profile/${user?.id}`} onClick={closeSidebar} className="group mb-6 flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-3 transition-all hover:border-purple-100 hover:bg-purple-50/60">
            <div className="relative shrink-0">
              <img src={getMediaUrl(user?.profile_img)} alt={`${user?.firstname || ""} ${user?.lastname || ""}`} className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow-sm" />
              <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-green-500" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{user?.firstname} {user?.lastname}</p>
              <p className="mt-0.5 text-xs text-gray-500">View your profile</p>
            </div>

            <MdChevronRight size={20} className="text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-purple-600" />
          </Link>

          {/* ================= NAVIGATION ================= */}

          <div className="mb-2 px-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400">Menu</p>
          </div>

          <nav className="flex-1">
            <ul className="space-y-1">
              {leftSidebarLinks.map((link) => {
                const isActive = pathname === link.route || pathname.startsWith(`${link.route}/`);

                return (
                  <li key={link.label}>
                    <NavLink to={link.route} onClick={closeSidebar} className={`group relative flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200 ${isActive ? "bg-purple-50 text-purple-700" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
                      {isActive && <span className="absolute -left-4 top-1/2 h-7 w-1 -translate-y-1/2 rounded-r-full bg-purple-600" />}

                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all ${isActive ? "bg-purple-100" : "bg-gray-50 group-hover:bg-gray-100"}`}>
                        <img src={link.imgURL} alt={link.label} className={`h-[19px] w-[19px] object-contain transition-all ${isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100"}`} />
                      </div>

                      <span className="flex-1">{link.label}</span>
                      {isActive && <span className="h-1.5 w-1.5 rounded-full bg-purple-600" />}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* ================= BOTTOM SECTION ================= */}

          <div className="mt-6 border-t border-gray-100 pt-4">
            <div className="mb-3 flex items-center gap-3 px-3">
              <img src={getMediaUrl(user?.profile_img)} alt={`${user?.firstname || ""} ${user?.lastname || ""}`} className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow-sm" />

              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-gray-700">{user?.firstname} {user?.lastname}</p>
                <p className="text-[11px] text-gray-400">Signed in</p>
              </div>
            </div>

            {/* ================= ACCOUNT SETTINGS ================= */}

            <NavLink
              to="/settings/account"
              onClick={closeSidebar}
              className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
                isAccountActive
                  ? "bg-purple-50 text-purple-700"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-lg transition-all ${
                  isAccountActive
                    ? "bg-purple-100 text-purple-700"
                    : "bg-gray-50 text-gray-400 group-hover:bg-purple-50 group-hover:text-purple-600"
                }`}
              >
                <MdSettings size={20} />
              </div>

              <span className="flex-1">Account</span>

              {isAccountActive && (
                <MdChevronRight size={18} className="text-purple-600" />
              )}
            </NavLink>

            {/* ================= LOGOUT ================= */}

            <button type="button" onClick={logout} className="group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-gray-500 transition-all hover:bg-red-50 hover:text-red-600 active:scale-[0.98]">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 transition group-hover:bg-red-100">
                <MdLogout size={20} />
              </div>
              <span>Log out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default LeftSidebar_mobile;
